import { useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FaEdit, FaSave, FaTimes } from "react-icons/fa"
import { Textarea } from "@/components/ui/textarea"
import { ResourceType } from "../Interfaces"
import { update_resource_metadata } from "../API"

interface DescriptionDialogProps {
    isOpen: boolean
    onClose: () => void
    fileName: string
    resourceType: ResourceType
    currentDescription?: string
    onDescriptionUpdated: () => void
}

export function DescriptionDialog({
    isOpen,
    onClose,
    fileName,
    resourceType,
    currentDescription = "",
    onDescriptionUpdated
}: DescriptionDialogProps) {
    const [description, setDescription] = useState(currentDescription)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSave = async () => {
        setIsLoading(true)
        setError(null)
        
        try {
            await update_resource_metadata(fileName, resourceType, description)
            onDescriptionUpdated()
            onClose()
        } catch (error) {
            console.error('Failed to update description:', error)
            setError('Failed to update description. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCancel = () => {
        setDescription(currentDescription)
        setError(null)
        onClose()
    }

    return (
        <div onClick={(e) => e.stopPropagation()}>
        <Dialog open={isOpen} onOpenChange={handleCancel}>
            <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FaEdit className="w-5 h-5 text-orange-500" />
                        Edit Description
                    </DialogTitle>
                    <DialogDescription>
                        Add or update the description for "{fileName}"
                    </DialogDescription>
                </DialogHeader>
                
                <div className="mt-4">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}
                    
                    <div className="space-y-2">
                        <label htmlFor="description" className="text-sm font-medium text-gray-700">
                            Description
                        </label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Enter a description for this resource..."
                            className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isLoading}
                            maxLength={500}
                        />
                        <div className="text-xs text-gray-500">
                            {description.length}/500 characters
                        </div>
                    </div>
                </div>
                
                <DialogFooter className="gap-2">
                    <Button 
                        variant="outline" 
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="flex-1"
                    >
                        <FaTimes className="w-4 h-4 mr-2" />
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading || description.length > 500}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <FaSave className="w-4 h-4 mr-2" />
                                Save Description
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </div>
    )
}
